import axios from "axios";

export function buildPlannerLogsApiUrl(domain) {
  let baseUrl = domain || "";
  if (!baseUrl) {
    baseUrl = "https://apiv26.myskool.app/api/";
  }
  if (!baseUrl.endsWith("/")) baseUrl += "/";
  return `${baseUrl}PlannerDayWise/GetPlannerLogs`;
}

function isCampusGroupBlock(item) {
  return item != null && Array.isArray(item.logs);
}

export function formatCampusSectionTitle(block) {
  if (!block) return "Campus";
  const branch = String(block.campusBranch || "").trim();
  const name = String(block.campusName || "").trim();
  if (name && branch && name !== branch) {
    return `${name} — ${branch}`;
  }
  return branch || name || `Campus ${block.campusID ?? ""}`.trim();
}

export function parsePlannerLogsResponse(raw) {
  if (raw == null) {
    return {
      ok: false,
      message: "Empty response from server.",
      rows: [],
      campusGroups: [],
      groupedByCampus: false,
    };
  }

  const root = raw.data != null && raw.status != null ? raw : raw;
  const status = String(root.status || "").toLowerCase();

  if (status === "error" || status === "failed" || status === "fail") {
    return {
      ok: false,
      message:
        root.message ||
        root.msg ||
        root.error ||
        "Server returned an error.",
      rows: [],
      campusGroups: [],
      groupedByCampus: false,
    };
  }

  let rows = root.data;
  if (rows == null && Array.isArray(root)) rows = root;
  if (rows == null && Array.isArray(raw)) rows = raw;
  if (rows && !Array.isArray(rows) && Array.isArray(rows.items)) {
    rows = rows.items;
  }

  if (!Array.isArray(rows)) {
    return {
      ok: false,
      message: "Unexpected data format from planner logs API.",
      rows: [],
      campusGroups: [],
      groupedByCampus: false,
    };
  }

  if (rows.length > 0 && isCampusGroupBlock(rows[0])) {
    const campusGroups = rows.map((block) => ({
      campusID: block.campusID,
      campusName: block.campusName,
      campusBranch: block.campusBranch,
      count: block.count,
      logs: Array.isArray(block.logs) ? block.logs : [],
    }));
    const flatRows = campusGroups.flatMap((g) => g.logs);
    return {
      ok: true,
      rows: flatRows,
      campusGroups,
      groupedByCampus: true,
      message: "",
    };
  }

  return {
    ok: true,
    rows,
    campusGroups: [],
    groupedByCampus: false,
    message: "",
  };
}

export function buildCampusSections(parsed) {
  if (!parsed || !parsed.ok) {
    return [];
  }

  if (parsed.groupedByCampus && parsed.campusGroups.length) {
    return parsed.campusGroups.map((g, gi) => ({
      key: `campus-${g.campusID != null ? g.campusID : gi}`,
      campusID: g.campusID,
      title: formatCampusSectionTitle(g),
      count:
        g.count != null && g.count !== ""
          ? Number(g.count)
          : (g.logs || []).length,
      logs: (g.logs || []).map((row, i) => normalizePlannerLogRow(row, i)),
    }));
  }

  const logs = (parsed.rows || []).map((row, i) =>
    normalizePlannerLogRow(row, i)
  );
  return [
    {
      key: "single",
      campusID: null,
      title: null,
      count: logs.length,
      logs,
    },
  ];
}

export function normalizePlannerLogRow(row, index) {
  const r = row || {};
  const teacherName =
    r.teacherName ||
    r.staffName ||
    r.teacher ||
    r.name ||
    r.staff ||
    r.employeeName ||
    r.userName ||
    "—";

  return {
    _key: r.id || r.plannerID || r.detailID || `row-${index}`,
    dateFrom: r.dateFrom || r.fromDate || r.plannerDateFrom,
    dateTo: r.dateTo || r.toDate || r.plannerDateTo,
    completedDate:
      r.completedDate ||
      r.completionDate ||
      r.dateCompleted ||
      r.completedOn,
    teacherName,
    className: r.className || r.class || r.gradeName || r.grade || "—",
    subjectName: r.subjectName || r.subject || "—",
    mainTopic:
      r.mainTopicName || r.topic || r.mainTopic || r.activity || "—",
    subTopic: r.subTopicName || r.subTopic || "",
    status: r.status || "—",
    notes: r.note || r.log || r.remarks || r.notes || "—",
  };
}

export function describePlannerLogsRequestError(err) {
  if (err.code === "ECONNABORTED") {
    return "The request timed out. Try again or narrow the date range.";
  }
  if (!err.response) {
    return "Network error — check your connection and try again.";
  }
  const status = err.response.status;
  const data = err.response.data;
  const apiMsg =
    (data && (data.message || data.msg || data.error || data.title)) || "";

  if (status === 401 || status === 403) {
    return "Session expired or access denied. Refresh the app and try again.";
  }
  if (status === 404) {
    return (
      apiMsg ||
      "Planner logs API was not found. Confirm GetPlannerLogs is deployed."
    );
  }
  if (apiMsg) return apiMsg;
  return `Server error (${status}). Please try again or contact support.`;
}

export async function fetchPlannerLogs({
  domain,
  token,
  db,
  campusID,
  tID,
  dtFrom,
  dtTo,
}) {
  const url = buildPlannerLogsApiUrl(domain);
  const response = await axios.get(url, {
    params: {
      db,
      campusID,
      tID: tID || 0,
      dtFrom,
      dtTo,
    },
    headers: {
      Authorization: `Bearer ${token}`,
      accept: "application/json",
    },
    timeout: 120000,
  });
  return parsePlannerLogsResponse(response.data);
}

function formatExportDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatExportDateTime(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  return (
    formatExportDate(dateStr) +
    " " +
    d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
  );
}

export function plannerLogsToExcelRows(logs, { includeTeacher = true } = {}) {
  return (logs || []).map((log, index) => {
    const base = {
      "S#": index + 1,
      "Planner From": formatExportDate(log.dateFrom),
      "Planner To": formatExportDate(log.dateTo),
      "Completion Date": formatExportDateTime(log.completedDate),
      Class: log.className,
      Subject: log.subjectName,
      Topic: log.mainTopic,
      "Sub Topic": log.subTopic || "",
      Status: log.status,
      Notes: log.notes,
    };
    if (includeTeacher) {
      return {
        "S#": base["S#"],
        "Planner From": base["Planner From"],
        "Planner To": base["Planner To"],
        "Completion Date": base["Completion Date"],
        Teacher: log.teacherName,
        Class: base.Class,
        Subject: base.Subject,
        Topic: base.Topic,
        "Sub Topic": base["Sub Topic"],
        Status: base.Status,
        Notes: base.Notes,
      };
    }
    return base;
  });
}

export function plannerLogsExcelSheetName(title, index) {
  let name = String(title || `Campus ${index + 1}`)
    .replace(/[\\/*?:[\]]/g, " ")
    .trim();
  if (!name) name = `Campus ${index + 1}`;
  return name.slice(0, 31);
}

function uniqueExcelSheetName(baseName, usedNames) {
  let name = baseName;
  let n = 2;
  while (usedNames.has(name)) {
    const suffix = ` ${n}`;
    name = baseName.slice(0, 31 - suffix.length) + suffix;
    n += 1;
  }
  usedNames.add(name);
  return name;
}

/** Writes planner logs xlsx; one sheet per campus when API returns grouped data. */
export async function exportPlannerLogsWorkbookFile(
  parsed,
  filename,
  { includeTeacher = true } = {}
) {
  const XLSX = await import("xlsx");
  const sections = buildCampusSections(parsed);
  const wb = XLSX.utils.book_new();
  const usedNames = new Set();

  const appendSection = (sec, index) => {
    const rows = plannerLogsToExcelRows(sec.logs, { includeTeacher });
    if (!rows.length) return;
    const ws = XLSX.utils.json_to_sheet(rows);
    const sheetLabel =
      parsed.groupedByCampus && sec.title
        ? sec.title
        : "Planner Logs";
    const sheetName = uniqueExcelSheetName(
      plannerLogsExcelSheetName(sheetLabel, index),
      usedNames
    );
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  };

  if (parsed.groupedByCampus && sections.length) {
    sections.forEach((sec, i) => appendSection(sec, i));
  } else {
    appendSection(sections[0] || { logs: [] }, 0);
  }

  if (!wb.SheetNames.length) {
    return false;
  }

  XLSX.writeFile(wb, filename);
  return true;
}
